import React, { useEffect, useState, useCallback } from 'react';
import { sarAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileDrop, StatCard } from '../components/import/ImportPrimitives';
import { Upload, Loader2, ShieldCheck, Lock, Clock, FileCheck2, AlertTriangle } from 'lucide-react';
import { extractErrorMessage } from '../lib/errors';
import OvertimeParity from './PayrollSARTabs/OvertimeParity';
import HoursComparison from './PayrollSARTabs/HoursComparison';
import GrossPayParity from './PayrollSARTabs/GrossPayParity';
import PayrollSetup from './PayrollSARTabs/PayrollSetup';

const fmtDate = (iso) => (iso ? iso : '—');
const n = (v) => (v === null || v === undefined ? '—' : String(v));
// SAR-reported dollar totals → money with thousands separators (item 13).
const fmtDollars = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const num = Number(v);
  return Number.isNaN(num) ? String(v)
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

// Exactly one mutually-exclusive status per import (item 14).
function importStatus(imp) {
  if (imp.is_final) return { label: 'Final (of record)', className: 'bg-indigo-600', icon: true };
  if (imp.status === 'superseded') return { label: 'Superseded', variant: 'secondary' };
  return { label: 'Draft', variant: 'outline' };
}

function ReadOnlyBanner() {
  return (
    <Alert className="border-sky-200 bg-sky-50" data-testid="sar-readonly-banner">
      <ShieldCheck className="h-4 w-4 text-sky-600" />
      <AlertDescription className="text-sky-800 text-sm">
        <strong>Read-only parallel test.</strong> Uploading a Scheduling Activity Report
        stores it for side-by-side comparison only. Nothing here changes schedules,
        timesheets, pay, invoices or the ledger.
      </AlertDescription>
    </Alert>
  );
}

function UnmatchedCard({ title, values, names }) {
  return (
    <Card data-testid={`sar-unmatched-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          {values.length > 0 ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <FileCheck2 className="w-4 h-4 text-emerald-500" />}
          {title} <Badge variant={values.length ? 'destructive' : 'secondary'}>{values.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {values.length === 0 ? (
          <p className="text-xs text-slate-500">All matched against existing records.</p>
        ) : (
          <ul className="text-xs text-slate-700 space-y-1 max-h-40 overflow-auto">
            {values.map((v) => (
              <li key={v} className="font-mono">{v}{names && names[v] ? ` — ${names[v]}` : ''}</li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ImportReport({ imp, onMarkFinal }) {
  if (!imp) return null;
  const t = imp.totals || {};
  const m = imp.match_report || {};
  return (
    <div className="space-y-5" data-testid="sar-import-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-900">{imp.filename}</h3>
          <Badge variant="outline">v{imp.version_no}</Badge>
          {(() => { const s = importStatus(imp); return (
            <Badge className={s.className} variant={s.variant} data-testid="sar-report-status">
              {s.icon && <Lock className="w-3 h-3 mr-1" />}{s.label}
            </Badge>
          ); })()}
          {imp.source_file && <Badge variant="outline" className="text-emerald-700 border-emerald-300"><Lock className="w-3 h-3 mr-1" />Source encrypted</Badge>}
        </div>
        {!imp.is_final && imp.status === 'active' && (
          <Button size="sm" variant="outline" onClick={() => onMarkFinal(imp.id)} data-testid="sar-mark-final-btn">
            <Lock className="w-4 h-4 mr-1" /> Mark as version of record
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Rows" value={n(t.row_count)} testId="sar-stat-rows" />
        <StatCard label="Date range" value={`${fmtDate(t.date_from)} → ${fmtDate(t.date_to)}`} testId="sar-stat-dates" />
        <StatCard label="Employees" value={n(t.distinct_employees)} testId="sar-stat-emps" />
        <StatCard label="Jobs" value={n(t.distinct_jobs)} testId="sar-stat-jobs" />
        <StatCard label="Paid hours" value={n(t.sum_paid_hours)} tone="text-sky-700" testId="sar-stat-paid" />
        <StatCard label="Overtime hours" value={n(t.sum_overtime_hours)} tone="text-amber-700" testId="sar-stat-ot" />
        <StatCard label="Double-time hours" value={n(t.sum_doubletime_hours)} testId="sar-stat-dt" />
        <StatCard label="Pay $" value={fmtDollars(t.sum_pay_dollars)} testId="sar-stat-pay" />
        <StatCard label="OT $" value={fmtDollars(t.sum_ot_dollars)} testId="sar-stat-otd" />
        <StatCard label="Billable $" value={fmtDollars(t.sum_billable_dollars)} testId="sar-stat-bill" />
        <StatCard label="Overnight rows" value={n(t.overnight_rows)} testId="sar-stat-overnight" />
        <StatCard label="$0 salaried rows" value={n(t.zero_dollar_salaried_rows)} testId="sar-stat-zero" />
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <UnmatchedCard title="Unmatched Employees" values={m.unmatched_employee_numbers || []} names={m.unmatched_employee_names} />
        <UnmatchedCard title="Unmatched Jobs" values={m.unmatched_job_numbers || []} />
        <UnmatchedCard title="Unmatched Hours Types" values={m.unmatched_hours_type_ids || []} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Reconciliation status</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <ul className="text-sm space-y-1">
              {Object.entries(t.reconciliation_status_counts || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-medium">{v}</span></li>
              ))}
              {Object.keys(t.reconciliation_status_counts || {}).length === 0 && <li className="text-xs text-slate-500">—</li>}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Non-billable hours by type</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <ul className="text-sm space-y-1">
              {Object.entries(t.nonbillable_hours_by_type || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-medium">{v}</span></li>
              ))}
              {Object.keys(t.nonbillable_hours_by_type || {}).length === 0 && <li className="text-xs text-slate-500">None</li>}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ImportTab() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imports, setImports] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await sarAPI.listImports(true);
      setImports(data.imports || []);
      if ((data.imports || []).length && !selected) setSelected(data.imports[0]);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not load imports'));
    }
  }, [selected]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async () => {
    if (!file) { toast.error('Choose a Scheduling Activity Report CSV'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await sarAPI.upload(fd);
      toast.success(`Imported ${data.totals.row_count} rows (v${data.version_no})`);
      setFile(null);
      setSelected(data);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object') {
        const base = (typeof d.message === 'string' && d.message) ? d.message : extractErrorMessage(e, 'Import failed');
        const miss = d.missing_columns?.length ? ` Missing: ${d.missing_columns.slice(0, 6).join(', ')}${d.missing_columns.length > 6 ? '…' : ''}.` : '';
        const unexp = d.unexpected_columns?.length ? ` Unexpected: ${d.unexpected_columns.slice(0, 6).join(', ')}.` : '';
        toast.error(`${base}${miss}${unexp}`);
      } else {
        toast.error(extractErrorMessage(e, 'Import failed'));
      }
    } finally {
      setUploading(false);
    }
  };

  const markFinal = async (id) => {
    try {
      const { data } = await sarAPI.markFinal(id, true);
      toast.success('Marked final (post-lock)');
      setSelected(data);
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not mark as version of record'));
    }
  };

  return (
    <div className="space-y-6" data-testid="sar-import-tab">
      <ReadOnlyBanner />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import Scheduling Activity Report</CardTitle>
          <CardDescription>
            Pull the SAR from WinTeam <strong>after payroll is locked</strong> — after the 6th
            for the 16th–end of the prior month, or after the 21st for the 1st–15th — then upload
            that CSV here. The exact 73-column SAR layout is required. A re-upload for the same
            dates supersedes the older version automatically; use <em>Mark as version of record</em>{' '}
            to lock the one you trust.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileDrop
            label="Scheduling Activity Report (CSV)"
            hint="Drop the SAR export, or click to choose"
            accept=".csv,text/csv"
            file={file}
            onFile={setFile}
            testId="sar-filedrop"
          />
          <Button onClick={upload} disabled={uploading || !file} data-testid="sar-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Importing…' : 'Import & Compare'}
          </Button>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <Card data-testid="sar-import-history">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Import history</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {imports.length === 0 ? (
              <p className="text-xs text-slate-500">No imports yet.</p>
            ) : (
              <ul className="space-y-1">
                {imports.map((imp) => (
                  <li key={imp.id}>
                    <button
                      onClick={() => setSelected(imp)}
                      data-testid={`sar-history-item-${imp.id}`}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${selected?.id === imp.id ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-700'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{imp.filename}</span>
                        <span className="text-xs opacity-70">v{imp.version_no}</span>
                      </div>
                      <div className="text-xs opacity-70">{fmtDate(imp.date_from)} → {fmtDate(imp.date_to)}</div>
                      <div className="mt-1 flex gap-1">
                        {(() => { const s = importStatus(imp); return (
                          <Badge className={`text-[10px] py-0 ${s.className || ''}`} variant={s.variant} data-testid={`sar-history-status-${imp.id}`}>{s.label}</Badge>
                        ); })()}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div>
          {selected ? <ImportReport imp={selected} onMarkFinal={markFinal} /> : (
            <Card><CardContent className="py-12 text-center text-slate-500 text-sm">Upload or select an import to see its report.</CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PayrollSAR({ tab = 'import' }) {
  if (tab === 'overtime') return <OvertimeParity />;
  if (tab === 'hours') return <HoursComparison />;
  if (tab === 'gross-pay') return <GrossPayParity />;
  if (tab === 'setup') return <PayrollSetup />;
  return <ImportTab />;
}
